import { Box } from "@dxp-feed/ui/Box";
import { Text } from "@dxp-feed/ui/Text";
import OffersCardHeader from "@/templates/offers/_components/OffersCardHeader";
import { formatDay } from "@/utils/dateFormat";
import { Button } from "@dxp-feed/ui/Button";
import {
  useAppDispatch,
  useAppSelector,
  useEffect,
  useRef,
  useState,
} from "@/utils/hooks/reactUseCustomHooks";
import { SyntheticEvent } from "react";
import { addOffersToList, deleteOffersFromList } from "@/services/mulesoft";
import { RootState } from "@/utils/redux/store";
import { dataLayerOffers } from "@/helpers/dataLayer";
import ImageWrapper from "@/components/ImageWrapper";
import { removeHTMLTags, stripHtmlTags } from "@/helpers/useCommonFunctions";
import {
  offersRemovedFromStorage,
  offersToBeAddedFromStorage,
  updateFavouriteOffers,
} from "@/utils/redux/favOffers";
import { useGigyaAccountInfo } from "@/helpers/gigyaCentralSsoHook";

export default function OffersDetailSliderCard({
  headingParam,
  featuredCardData,
  offerAddedInList,
  openDialog,
  isHash,
  onVisibilityChange,
  setIsPanelOpen,
  posOfferData,
  setGroupOffers,
  setIsOpen,
  setFeaturedOffer,
  fromShoppingList,
  closeBottomSheet
}: any & { headingParam?: any }) {
  const { loggedIn: userLoggedIn } = useAppSelector(
    (state: RootState) => state.gigyaslice
  );

  const [flipped, setFlipped] = useState(false); // State to manage flip
  const [offersQuantity, setOfferQuantity] = useState(
    offerAddedInList === 1 ? true : false
  );
  const offers =
    typeof window != "undefined"
      ? sessionStorage.getItem("offers") || "[]"
      : "[]";
  const [isActive, setIsActive] = useState(false); // State to control Terms & Conditions visibility
  const [isLoaded, setIsLoaded] = useState(false); // State to track if component is loaded
  const flipButtonRef = useRef<HTMLButtonElement>(null); // Ref for the flip button
  const closeButtonRef = useRef<HTMLButtonElement>(null); // Ref for the close button
  const termsButtonRef = useRef<HTMLButtonElement>(null); // Ref for Terms & Conditions button
  const cardRef = useRef<HTMLDivElement>(null); // Ref for the entire card component
  const { favOffers } = useAppSelector((state) => state.favoirateOffersSlice);
  const dispatch = useAppDispatch();

  const [loginModalOpen, setloginModalOpen] = useState(false);
  const [showQuantityText, setShowQuantityText] = useState(false);
  const [Quantity, setQuantity] = useState(offerAddedInList);
  const [token, setToken] = useState("");
  const managementListData: any = useAppSelector(
    (state) => state?.manageListSlice
  );
  const favouriteList: any = useAppSelector(
    (state) => state?.favouriteProductSlice
  );
  const handleLoginModal = () => {
    setloginModalOpen((prev) => !prev);
    setShowQuantityText(false);
  };
  const shoppingListData: any = useAppSelector(
    (state) => state?.cartProductSlice
  );
  // Set component as loaded after mount
  useEffect(() => {
    setQuantity(offerAddedInList);
    setOfferQuantity(offerAddedInList === 1);
    if (isHash && !userLoggedIn) {
      const existingOffers: any[] = JSON.parse(offers);
      const filter = existingOffers.filter(
        (itm) => itm.offerId == featuredCardData?.offerId
      );
      if (filter?.length > 0) {
        setQuantity(1);
        setOfferQuantity(true);
      } else {
        setQuantity(0);
        setOfferQuantity(false);
      }
    }
  }, [offerAddedInList, offers]);

  // Modified toggleFlip to handle focus management properly
  const toggleFlip = (e: SyntheticEvent) => {
    e.stopPropagation(); // Prevent event bubbling

    if (!flipped) {
      // When opening flip panel, set focus to the back panel after flip animation
      setFlipped(true);
      // Wait for animation to complete before focusing
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 500);
    } else {
      // When closing flip panel, we'll focus back to the original button
      setFlipped(false);
      // Wait for animation to complete before focusing
      setTimeout(() => {
        flipButtonRef.current?.focus();
      }, 500);
    }
  };

  const toggleActiveState = (e?: SyntheticEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent event bubbling
    }
    setIsActive((prev) => !prev);
  };

  const closeActiveState = (e?: SyntheticEvent) => {
    if (e) {
      e.stopPropagation(); // Prevent event bubbling
    }
    setIsActive(false);
    // Return focus to the terms button
    setTimeout(() => {
      termsButtonRef.current?.focus();
    }, 300);
  };

  // Handle Escape key for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (isActive) {
        closeActiveState();
      } else if (flipped) {
        toggleFlip(e);
      }
    }
  };

  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const handleImageError = () => {
    setImageSrc("/offer_not_found.svg"); // Set fallback image on error
  };
  const addOffersToStorage = () => {
    const storedOffers = sessionStorage.getItem("offers") || "[]";
    const existingOffers: any[] = JSON.parse(storedOffers);

    const isDuplicate = existingOffers.find(
      (offer) => offer.offerId === featuredCardData.offerId
    );

    if (!isDuplicate) {
      existingOffers.push(featuredCardData);
      sessionStorage.setItem("offers", JSON.stringify(existingOffers));
      dispatch(offersToBeAddedFromStorage(existingOffers));
    }
  };

  const deleteOffersFromStorage = () => {
    const storedOffers = sessionStorage.getItem("offers") || "[]";
    const remainingOffers = JSON.parse(storedOffers).filter(
      (offer: any) => offer.offerId !== featuredCardData.offerId
    );
    sessionStorage.setItem("offers", JSON.stringify(remainingOffers));
    dispatch(offersRemovedFromStorage(remainingOffers));
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Notify the parent component about the visibility change
        const filteredProduct = (({ offerId, description }) => ({
          item_id: offerId,
          item_name: description,
          affiliation: "Personalized Offers",
        }))(featuredCardData);

        if (onVisibilityChange) {
          onVisibilityChange(filteredProduct, entry.isIntersecting);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      }
    );
    if (cardRef.current) {
      observer.observe(cardRef.current);
    }
    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  const gigyaToken = useGigyaAccountInfo();

  useEffect(() => {
    const fetchToken = async () => {
      const token = await gigyaToken();
      setToken(token.idToken);
    };

    fetchToken();
  }, [offerAddedInList]);

  const handleCardClick = (e: any) => {
    const isIconCardClick =
      e.target.closest(".icon-card") || e.target.closest(".icon-card-filled");
    if (isIconCardClick) {
      return;
    }
    setIsOpen(true);
  };

  return (
    <>
      <Box
        as="div"
        ref={cardRef}
        className={`relative flex w-full`}
        onKeyDown={handleKeyDown}
        onClick={handleCardClick}
      >
        <Box
          as="div"
          className={`w-full rounded-[0px] bg-lightpurple300 overflow-y-auto h-full pb-6 max-h-[calc(100vh-180px)]`}
        >
          {/* Front Side */}
          <Box
            as="div"
            className={` frontdata relative bg-brandWhite text-brandBlack flex flex-col justify-start`}
          >
            <Box className="showTermHeader">
              <OffersCardHeader
                headerOffers={featuredCardData}
                showTerms={true}
              />
            </Box>

                  <Text
                    as="p"
                    className="relative text-sm text-grey700 pb-2 text-left pt-4 px-4"
                  >
                    {Number(featuredCardData?.daysLeft) === 1 ? (
                      "Expires Today"
                    ) : (
                      <>
                        <strong>{featuredCardData?.daysLeft}</strong> days left
                      </>
                    )}
                  </Text>
            <Box
              as="div"
              className={`relative items-center justify-center px-4 py-[22px] pb-[36px]  min-h-[200px]`}
            >
              <ImageWrapper
                src={featuredCardData?.imageName}
                width={188}
                height={120}
                className={`mx-auto mt-[4px] object-contain h-[160px] w-[200px]`}
                alt={featuredCardData.alt ?? ""}
                onError={handleImageError}
              />
            </Box>
             <Text
                    as="p"
                    className="text-sm relative pt-1  gap-[4px] flex items-center w-full text-left leading-3 justify-between px-4 pb-4"
                  >
                    <Text
                      as="span"
                      className="flex flex-row items-center gap-1 max-w-[80%] sm:max-w-full"
                    >
                      <Text
                        as="span"
                        className="icon-check text-white bg-green900 w-[16px] h-[16px] min-w-[16px] min-h-[16px] rounded-full flex items-center justify-center pr-[2px] text-[8px]"
                      ></Text>
                      Loaded on Card
                    </Text>
                  </Text>
            <Box
              as="div"
              className="bg-lightpurple300 h-full pt-4 pb-4 mb-0 px-4"
            >
              <Box as="div" className={`flex flex-col`}>
                <Box as="div" className="text-left pb-2">
                  {featuredCardData?.offerLimits?.customer?.limit > 1 && (
                    <Box
                      as="div"
                      className="pt-2 pb-1 flex flex-col items-center justify-center mt-0 mb-0"
                    >
                      <Box
                        as="div"
                        className="flex gap-[4px] items-center justify-center"
                      >
                        {Array.from(
                          {
                            length:
                              featuredCardData?.offerLimits?.customer?.limit,
                          },
                          (_, index) => (
                            <Text
                              key={index}
                              as="span"
                              className={`rounded-[50%] inline-flex pt-[3px] pl-[1.5px] text-[8px] w-[14px] h-[14px] ${
                                index <
                                featuredCardData?.offerLimits?.customer
                                  ?.progress
                                  ? "bg-black text-white icon-check"
                                  : "bg-grey300 text-brandBlack"
                              }`}
                            ></Text>
                          )
                        )}
                      </Box>
                      <Text as="span" className="text-grey700 text-xs mt-2">
                        {`${featuredCardData?.offerLimits?.customer?.limit - featuredCardData?.offerLimits?.customer?.progress} ${
                          featuredCardData?.offerLimits?.customer?.limit -
                            featuredCardData?.offerLimits?.customer
                              ?.progress ===
                          1
                            ? "use"
                            : "uses"
                        } left`}
                      </Text>
                    </Box>
                  )}
                  <Text
                    as="p"
                    className={`text-brandBlack pt-[4px] pb-[0px text-body font-bold pb-[10px]`}
                    dangerouslySetInnerHTML={{
                      __html:
                        removeHTMLTags(featuredCardData?.description).length >
                        90
                          ? featuredCardData.description.slice(0, 90) + "..."
                          : featuredCardData.description || "",
                    }}
                  ></Text>
                  {/* {posOfferData?.[featuredCardData?.offerId]?.length > 1 && (
                    <>
                      <Button
                        variant="icon"
                        tabIndex={0}
                        className="text-brandSecondary text-left flex !justify-start leading-[18px]z-0 font-bold !text-[12px] underline !px-0 !pt-[1px] mt-[0px] mb-[4px] !pb-[6px] w-full hover:no-underline"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent flip when clicking the button
                          setIsPanelOpen(true);
                          setGroupOffers(
                            posOfferData[featuredCardData?.offerId]
                          );
                          setFeaturedOffer(featuredCardData);
                          setIsOpen(false);
                        }}
                      >
                        <ImageWrapper
                          src="/home/scene-plus.svg"
                          width={12}
                          height={12}
                          className="mr-1"
                          alt="scene plus image"
                        />{" "}
                        Explore Products
                      </Button>
                    </>
                  )} */}
                  {!(
                    featuredCardData?.offerType == 19 ||
                    featuredCardData?.offerType == 20 ||
                    featuredCardData?.offerType == 21
                  ) && (
                    <Button
                      variant="primary"
                      className="w-full capitalize font-normal border-brandPrimary bg-brandPrimary border-[1px] !text-body px-[24px] text-brandWhite hover:border-brandBlack hover:bg-brandPrimary hover:text-brandBlack focus:border-brandBlack focus:text-brandBlack my-2"
                      onClick={() => {
                        const maxQuantity = 1;
                        const quantity = Math.min(Quantity + 1, maxQuantity);
                        if (Quantity < maxQuantity) {
                          setQuantity(quantity);
                          if (!userLoggedIn && isHash) {
                            
                            if (quantity > 0) {
                              setOfferQuantity(true);
                              addOffersToStorage();
                            } else {
                              deleteOffersFromStorage();
                              setOfferQuantity(false);
                            }
                          } else {
                            
                            if (quantity > 0) {
                              openDialog && openDialog("addList");
                              setOfferQuantity(true);
                              const newOffer = {
                                offer_id: featuredCardData.offerId,
                                banner: process.env.NEXT_PUBLIC_BANNER as string,
                                offer_type: featuredCardData.offerType,
                                offer_description:
                                  JSON.stringify(featuredCardData),
                              };
                              const oldOffers =
                                favOffers?.favouriteOffers || [];
                              const updatedOffers = [...oldOffers, newOffer];
                              dispatch(updateFavouriteOffers(updatedOffers));
                              addOffersToList(newOffer, token);
                              dataLayerOffers({
                                event: "add_offer_to_list",
                                offer_name: stripHtmlTags(
                                  featuredCardData.description
                                ),
                                list_name: headingParam || "",
                                offer_list_name: "favourite_offers",
                                page_title: document.title,
                              });
                              return;
                            }
                          }
                        } else {
                          setQuantity(0);
                          setOfferQuantity(false);
                          openDialog && openDialog("removeList");
                          const filterOffers =
                            favOffers?.favouriteOffers?.filter(
                              (offer) =>
                                offer?.offer_id !== featuredCardData.offerId
                            ) || [];

                          const updatedOffers = [...filterOffers];
                          dispatch(updateFavouriteOffers(updatedOffers));
                          deleteOffersFromList({
                            offer_id: featuredCardData.offerId,
                            token,
                          });

                          dataLayerOffers({
                            event: "remove_offer_from_list",
                            offer_name: stripHtmlTags(
                              featuredCardData.description
                            ),
                            // list_name: headingParam || "",
                            offer_list_name: "favourite_offers",
                            page_title: document.title,
                          });

                          if (!userLoggedIn && isHash) {
                            deleteOffersFromStorage();
                          }
                          if (fromShoppingList) {
                            closeBottomSheet();
                          }
                        }
                      }}
                    >
                      {" "}
                      {offersQuantity ? "Remove from list" : "Add to List"}
                    </Button>
                  )}
                </Box>

                <Text
                  as="p"
                  className={`pt-0 mb-3 text-base text-left font-bold`}
                >
                  Terms & Conditions
                </Text>
                <Text as="p" className={`mb-4 text-sm text-left pb-2`}>
                  {featuredCardData?.tcsDescription ||
                    "No terms and conditions available."}
                </Text>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

    </>
  );
}
